/**
 * aides-professionnels.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /professionnels/aides/[slug].
 *
 * Audience : TPE / PME — locaux professionnels tertiaires (commerces,
 * cabinets, agences, ateliers, restaurants, hôtels indépendants…).
 * Tone : pragmatic-roi (direct, ROI, factuel, sans pathos).
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_PROFESSIONNELS: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-bat',
    iconName: 'sparkles',
    eyebrow: "Certificats d'Économies d'Énergie, secteur bâtiment tertiaire",
    title: 'CEE BAT pour locaux tertiaires',
    shortLabel: 'CEE BAT',
    lead: 'Des aides pour vos travaux dans vos locaux : isolation, chauffage, éclairage, ventilation. Accessible quelle que soit la taille de votre entreprise.',
    amount: 'Selon fiche & volume',
    amountLabel: "Versement par l'obligé après travaux",
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Sans condition de taille',
        description:
          "Accessible à toute structure disposant d'un local tertiaire : TPE, PME, profession libérale, propriétaire de murs.",
      },
      {
        iconName: 'file-text',
        label: 'Catalogue complet',
        description:
          'Chauffage (pompe à chaleur, ventilation, pilotage), enveloppe (isolation toiture, murs, planchers) et équipements (LED, variateurs, robinets thermostatiques).',
      },
      {
        iconName: 'file-signature',
        label: 'Demande avant le devis',
        description:
          "L'aide doit être demandée avant de signer le devis. Sinon, dossier non éligible.",
      },
      {
        iconName: 'wallet',
        label: 'Cumulable',
        description:
          "Cumul possible avec l'avantage fiscal, les prêts à taux préférentiel et certaines aides régionales.",
      },
    ],
    conditions: [
      "Structure professionnelle disposant d'un local tertiaire (occupant ou propriétaire)",
      'Travaux ou équipements éligibles à une fiche BAT-* applicable',
      "Demande d'aide avant signature du devis",
      'Conservation des justificatifs : devis, factures',
      'Réalisation par un professionnel disposant des qualifications requises',
    ],
    cumulableWith: [
      'Bonus chauffage tertiaire',
      'Avantage fiscal',
      'Prêts à taux préférentiel',
      'Aides régionales',
    ],
    faq: [
      {
        question: 'Quelles opérations sont éligibles pour mon local ?',
        answer:
          "Sur un local tertiaire courant, les opérations les plus fréquentes : pompe à chaleur (BAT-TH-104), ventilation double flux (BAT-TH-125), pilotage intelligent (BAT-TH-116), calorifugeage (BAT-TH-146), isolation toiture (BAT-EN-101) et éclairage LED (BAT-EQ-127). On identifie celles qui collent à votre activité et on chiffre l'aide.",
      },
      {
        question: "L'aide est-elle cumulable avec l'avantage fiscal ?",
        answer:
          "Oui. L'avantage fiscal est une déduction sur votre résultat imposable, indépendante de l'aide travaux. Les deux mécanismes peuvent se cumuler sur un même équipement éligible.",
      },
      {
        question: 'Faut-il un diagnostic préalable ?',
        answer:
          "Non, le diagnostic n'est pas exigé pour activer l'aide sur des opérations standard. Il devient utile dès que vous combinez plusieurs gestes ou si votre bâtiment a des obligations de réduction de consommation.",
      },
    ],
    ctaLabel: 'Estimer mes primes CEE BAT',
    ctaHref: '/professionnels/diagnostic-cee',
    secondaryCta: { label: 'Voir le catalogue BAT-*', href: '/cee/fiches?secteur=BAT' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'coup-de-pouce-cvc',
    iconName: 'flame',
    eyebrow: 'Bonification chauffage, ventilation et climatisation',
    title: 'Coup de pouce CVC tertiaire',
    shortLabel: 'Coup de pouce CVC',
    lead: "Un bonus qui s'ajoute à votre aide pour certains travaux : pompe à chaleur, ventilation double flux, raccordement à un réseau de chaleur. Si vous remplacez une vieille chaudière, c'est pour vous.",
    amount: 'Bonification CEE',
    amountLabel: 'Aide majorée pour opérations CVC ciblées',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Opérations ciblées',
        description:
          "Remplacement d'une chaudière fioul ou gaz par pompe à chaleur, raccordement à un réseau de chaleur, ventilation double flux avec récupération.",
      },
      {
        iconName: 'building-2',
        label: 'Spécifique tertiaire',
        description:
          "Ce bonus s'applique aux locaux tertiaires (commerces, bureaux, hôtels, restaurants) — distinct du bonus résidentiel.",
      },
      {
        iconName: 'sparkles',
        label: "S'ajoute à l'aide de base",
        description:
          "Le bonus s'ajoute à l'aide standard — c'est un complément, pas une aide séparée.",
      },
      {
        iconName: 'file-signature',
        label: 'Versé par un partenaire agréé',
        description: 'Le bonus est versé par un partenaire agréé pour ce dispositif.',
      },
    ],
    conditions: [
      'Opérations éligibles définies par le dispositif applicable',
      'Bâtiment tertiaire (commerce, cabinet, bureau, hôtel, restaurant…)',
      "Demande d'aide avant signature du devis",
      'Installateur qualifié pour le poste concerné',
      'Versement via un partenaire agréé',
    ],
    cumulableWith: [
      'Aide de base',
      'Avantage fiscal',
      'Prêts à taux préférentiel',
      'Aides régionales',
    ],
    faq: [
      {
        question: "Quelle différence entre l'aide de base et le bonus ?",
        answer:
          "L'aide de base est versée pour toute opération éligible. Le bonus est une majoration ciblée sur certaines opérations prioritaires (remplacement chaudière fioul, raccordement réseau de chaleur, ventilation performante). Les deux se cumulent automatiquement quand l'opération est doublement éligible.",
      },
      {
        question: 'Quelles opérations bénéficient du bonus ?',
        answer:
          "Les opérations éligibles évoluent au fil du temps. Historiquement, ce bonus a ciblé : le remplacement de chaudière fioul par une pompe à chaleur, le raccordement à un réseau de chaleur, et la mise en place d'une ventilation double flux avec récupération. Vérifiez les dispositifs applicables lors de votre demande.",
      },
      {
        question: 'Comment trouver un partenaire agréé ?',
        answer:
          'La liste des partenaires agréés est publiée par le ministère de la Transition écologique. Vous pouvez aussi passer par un délégataire qui gère le dépôt et vous décharge des démarches.',
      },
    ],
    ctaLabel: 'Estimer mon Coup de pouce CVC',
    ctaHref: '/professionnels/diagnostic-cee',
    secondaryCta: {
      label: 'Demander à être rappelé(e)',
      href: '/contact?source=professionnels&besoin=coup-de-pouce-cvc',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'suramortissement',
    iconName: 'percent',
    eyebrow: 'Avantage fiscal pour équipements de chaleur renouvelable',
    title: 'Suramortissement chaleur renouvelable',
    shortLabel: 'Suramortissement',
    lead: 'Un avantage fiscal qui vous permet de déduire une partie du coût de vos équipements énergétiques. Cumulable avec les aides.',
    amount: 'Déduction fiscale',
    amountLabel: "Selon nature de l'équipement et durée d'amortissement",
    keyPoints: [
      {
        iconName: 'percent',
        label: 'Avantage fiscal direct',
        description:
          "Déduction supplémentaire sur votre résultat imposable, en plus de l'amortissement comptable classique de l'équipement.",
      },
      {
        iconName: 'flame',
        label: 'Équipements chaleur ciblés',
        description:
          "Production de chaleur ou de froid à partir d'énergies renouvelables, récupération de chaleur, stockage thermique, réseaux de chaleur renouvelable.",
      },
      {
        iconName: 'building-2',
        label: 'Toutes les entreprises',
        description:
          'Ouvert aux sociétés et aux entreprises individuelles relevant des bénéfices industriels, commerciaux ou agricoles.',
      },
      {
        iconName: 'sparkles',
        label: 'Cumulable avec les aides',
        description:
          "L'avantage fiscal et l'aide travaux portent sur des assiettes différentes (déduction fiscale vs aide versée) et peuvent se combiner sur un même équipement.",
      },
    ],
    conditions: [
      "Entreprise soumise à l'impôt sur les sociétés ou sur le revenu (bénéfices industriels, commerciaux ou agricoles)",
      'Équipement éligible (production ou réseau de chaleur renouvelable, stockage thermique)',
      'Acquisition ou fabrication dans la période ouverte par la loi de finances applicable',
      "Inscription à l'actif immobilisé et amortissement selon les règles fiscales",
      'Documentation comptable et fiscale conservée pour justification',
    ],
    cumulableWith: [
      'Aide travaux',
      'Bonus chauffage tertiaire',
      'Prêts à taux préférentiel',
      'Aides régionales',
    ],
    faq: [
      {
        question: "Quels équipements sont éligibles à l'avantage fiscal ?",
        answer:
          "Les équipements de production de chaleur ou de froid à partir d'énergies renouvelables (biomasse, géothermie, solaire thermique, récupération de chaleur), les équipements de stockage thermique, et certains équipements de réseaux de chaleur. La liste exacte évolue dans le temps.",
      },
      {
        question: 'Puis-je cumuler avantage fiscal et aide travaux ?',
        answer:
          "Oui. L'avantage fiscal est une déduction sur votre résultat, l'aide travaux est un revenu. Les deux n'opèrent pas sur la même assiette. Documentez clairement le traitement comptable et fiscal de chaque flux.",
      },
      {
        question: "Comment activer l'avantage fiscal ?",
        answer:
          "Le dispositif s'applique directement sur votre déclaration fiscale, sans démarche préalable. Parlez-en à votre expert-comptable avant l'investissement, et conservez toutes les pièces justifiant l'éligibilité de l'équipement (factures, fiches techniques).",
      },
    ],
    ctaLabel: 'Identifier les équipements éligibles',
    ctaHref: '/professionnels/diagnostic-cee',
    secondaryCta: {
      label: 'En parler avec un conseiller',
      href: '/contact?source=professionnels&besoin=suramortissement',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'prets-bpifrance',
    iconName: 'banknote',
    eyebrow: 'Bpifrance, dispositif Climat',
    title: 'Prêts Bpifrance climat',
    shortLabel: 'Prêts Bpifrance',
    lead: 'Des prêts à conditions avantageuses pour financer vos travaux de performance énergétique. Sans garantie personnelle, pensés pour les TPE et PME.',
    amount: 'Variable',
    amountLabel: 'Montant, durée et taux selon le prêt et le projet',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Sans garantie personnelle',
        description:
          'La plupart des prêts sont accordés sans prise de garantie sur les actifs ni caution personnelle du dirigeant.',
      },
      {
        iconName: 'building-2',
        label: 'Cible TPE et PME',
        description:
          "Dispositifs accessibles aux PME (jusqu'à 250 salariés), ouverts à de nombreux secteurs : commerce, services, industrie légère, hôtellerie-restauration, professions libérales.",
      },
      {
        iconName: 'sparkles',
        label: 'Cumul avec aides et subventions',
        description:
          'Les prêts peuvent financer le reste à charge après aides et subventions — utile pour préserver votre trésorerie.',
      },
      {
        iconName: 'file-text',
        label: 'Dossier instruit par Bpifrance',
        description:
          'Vous montez un dossier de financement classique (business plan, devis, plan de financement). Décision après analyse.',
      },
    ],
    conditions: [
      "TPE ou PME (jusqu'à 250 salariés, CA ≤ 50 M€ ou bilan ≤ 43 M€)",
      'Projet à dimension énergie/environnement éligible au dispositif Climat',
      "Présentation d'un business plan, devis, plan de financement et derniers comptes",
      'Inscription au registre du commerce et situation à jour des obligations fiscales et sociales',
      "Conditions exactes (montant, durée, taux) variables selon le prêt et l'évaluation",
    ],
    cumulableWith: [
      'Aide travaux',
      'Bonus chauffage tertiaire',
      'Avantage fiscal',
      'Aides régionales',
    ],
    faq: [
      {
        question: 'Quels prêts financent la transition énergétique ?',
        answer:
          "Les principaux dispositifs sont le Prêt Vert (cofinancé avec l'ADEME, pour des projets de performance énergétique et environnementale) et le Prêt Économies d'Énergie (travaux d'efficacité énergétique). La liste à jour est sur bpifrance.fr.",
      },
      {
        question: "Quel est le délai d'obtention ?",
        answer:
          'Quelques semaines entre le dépôt et la décision, selon la complexité du projet. Compter ensuite un délai supplémentaire pour la mise à disposition des fonds. Déposez le dossier avant la signature des devis travaux.',
      },
      {
        question: 'Puis-je cumuler un prêt avec une aide ?',
        answer:
          "Oui, c'est un cas d'usage courant. Le prêt finance l'investissement, l'aide rembourse une partie après travaux. L'aide perçue vient réduire l'encours du prêt ou réintégrer la trésorerie. Dimensionnez bien votre plan de financement.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=professionnels&besoin=prets-bpifrance',
    secondaryCta: { label: 'Diagnostic CEE en parallèle', href: '/professionnels/diagnostic-cee' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement de l'électricité,",
    title: 'Optimisation du TURPE (acheminement)',
    shortLabel: 'Optimisation TURPE',
    lead: "La partie réseau de votre facture, souvent un quart à un tiers du total. Ça ne se négocie pas, mais ça s'optimise : on regarde vos puissances souscrites et vos dépassements.",
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque facture',
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Puissances souscrites ajustées',
        description:
          'On compare vos puissances souscrites à vos appels de puissance réels. Une puissance surdimensionnée se paie chaque mois pour rien.',
      },
      {
        iconName: 'file-text',
        label: 'Formule tarifaire adaptée',
        description:
          "Courte, moyenne ou longue utilisation : la formule tarifaire doit correspondre à votre profil de consommation. Un commerce ouvert 6 j/7 et un bureau fermé le week-end n'ont pas la même formule optimale.",
      },
      {
        iconName: 'trending-down',
        label: 'Dépassements maîtrisés',
        description:
          "Les dépassements de puissance sont facturés avec pénalité. On les identifie et on arbitre : augmenter la puissance ou lisser l'usage.",
      },
      {
        iconName: 'wallet',
        label: "Indépendant des fournisseurs d'énergie",
        description:
          "Le transport de l'électricité est identique quel que soit votre fournisseur. L'optimisation se cumule donc avec une mise en concurrence des offres.",
      },
    ],
    conditions: [
      'Analyse des 12 derniers mois de factures (ou des courbes de charge du compteur communicant)',
      "Applicable quel que soit votre fournisseur d'électricité",
      'Modification des paramètres via votre fournisseur (répercutée au gestionnaire de réseau)',
      'Étude au cas par cas des pénalités de dépassement et des options tarifaires',
    ],
    cumulableWith: [
      'Comparaison des offres de fourniture',
      'Exonération CSPE',
      'Aide travaux',
      'Aides régionales',
    ],
    faq: [
      {
        question: "C'est quoi le TURPE sur ma facture ?",
        answer:
          "C'est le péage payé pour l'utilisation du réseau public d'électricité (lignes, transformateurs, comptage). Il est fixé par la Commission de régulation de l'énergie et collecté par votre fournisseur — il apparaît dans la part « acheminement » de la facture. Personne ne peut le négocier, mais ses paramètres (puissances, version tarifaire) dépendent de votre contrat : c'est là que se joue l'optimisation.",
      },
      {
        question: 'Faut-il changer de fournisseur pour optimiser ?',
        answer:
          "Non. L'acheminement est strictement identique chez tous les fournisseurs. L'optimisation passe par un ajustement des paramètres de votre point de livraison, que votre fournisseur actuel peut appliquer. C'est complémentaire d'une mise en concurrence des offres.",
      },
      {
        question: 'Quelle économie puis-je espérer ?',
        answer:
          "Cela dépend de votre profil : un site bien calibré n'a rien à gagner, un site surdimensionné ou mal versionné peut réduire significativement sa part acheminement. C'est précisément ce que l'audit gratuit de vos factures permet de chiffrer — si on ne trouve rien, on vous le dit.",
      },
    ],
    ctaLabel: 'Auditer mes factures gratuitement',
    ctaHref: '/professionnels/comparateur-energie',
    secondaryCta: {
      label: 'En parler avec un conseiller',
      href: '/contact?source=professionnels&besoin=optimisation-turpe',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'exoneration-cspe',
    iconName: 'percent',
    eyebrow: "Fiscalité de l'électricité, taux réduits et exonérations,",
    title: "Exonération CSPE (accise sur l'électricité)",
    shortLabel: 'Exonération CSPE',
    lead: "Une taxe sur votre consommation d'électricité. Certaines activités paient moins, d'autres sont exonérées. Si vous avez trop payé, vous pouvez récupérer le trop-versé.",
    amount: 'Taux réduit ou exonération',
    amountLabel: "Selon l'activité et l'intensité électrique",
    keyPoints: [
      {
        iconName: 'percent',
        label: 'Taux réduits ciblés',
        description:
          "Les textes prévoient des taux réduits pour les entreprises fortement consommatrices d'électricité, selon le ratio entre consommation et valeur ajoutée.",
      },
      {
        iconName: 'cog',
        label: 'Usages exonérés',
        description:
          'Certains procédés sont exonérés : électrolyse, réduction chimique, procédés métallurgiques ou minéralogiques notamment.',
      },
      {
        iconName: 'banknote',
        label: 'Remboursement rétroactif',
        description:
          'Si vous étiez éligible mais avez payé le taux plein, une réclamation permet de récupérer le trop-versé sur les années récentes.',
      },
      {
        iconName: 'file-signature',
        label: 'Démarche déclarative',
        description:
          "L'application du bon taux passe par une attestation transmise à votre fournisseur. On prépare le dossier avec vous.",
      },
    ],
    conditions: [
      'Activité ou usage éligible (électro-intensité, procédés spécifiques, transport…)',
      "Calcul du ratio d'électro-intensité (consommation rapportée à la valeur ajoutée)",
      'Attestation transmise au fournisseur pour appliquer le bon taux',
      'Justificatifs conservés pour les demandes de remboursement',
    ],
    cumulableWith: [
      'Optimisation TURPE',
      'Comparaison des offres de fourniture',
      'Aide travaux',
      'Avantage fiscal',
    ],
    faq: [
      {
        question: 'CSPE, TICFE, accise : de quoi parle-t-on ?',
        answer:
          "C'est la même taxe sous trois noms successifs. L'ancienne CSPE a été fusionnée dans la TICFE, elle-même rebaptisée « accise sur l'électricité » et gérée par l'administration fiscale. Elle se paie sur chaque MWh consommé et figure sur votre facture.",
      },
      {
        question: 'Mon entreprise peut-elle prétendre à un taux réduit ?',
        answer:
          "Cela dépend de votre activité (certains procédés industriels sont exonérés par nature) et de votre intensité électrique (le rapport entre votre facture d'électricité et votre valeur ajoutée). On vérifie votre situation à partir de vos factures et de vos données comptables.",
      },
      {
        question: "Puis-je récupérer ce que j'ai payé en trop ?",
        answer:
          "Oui, si vous étiez éligible à un taux réduit mais avez été facturé au taux plein, une réclamation permet de récupérer le trop-versé. Le délai est limité — d'où l'intérêt de vérifier votre éligibilité sans attendre.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=professionnels&besoin=exoneration-cspe',
    secondaryCta: {
      label: 'Auditer mes factures en parallèle',
      href: '/professionnels/comparateur-energie',
    },
  },
];

export function getAideProfessionnelStub(slug: string): AideStub | undefined {
  return AIDES_PROFESSIONNELS.find((a) => a.slug === slug);
}
